import {
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormControl, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  faLink,
  faSpinner,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import {
  DialogCloseOptions,
  DialogCloseReason,
  injectDirtyCloseHandler,
} from '@sui/dialog';
import { SuiDateRange } from '@sui/date-picker';
import { Store } from '@ngrx/store';

import { FeedApiService } from 'src/app/api/services/feed-api.service';
import { PostApiService } from 'src/app/api/services/post-api.service';
import { SimpleDialogBase } from 'src/app/shared/abstract/simple-dialog-base';
import {
  FeedSettingsService,
  PropertyViewConfig,
} from 'src/app/core/services/feed-settings.service';

import { DropdownItem } from '../../../models/toolbar.models';

import { toUnix } from 'src/app/shared/utils/date-utils';
import { endOfDay } from 'date-fns';
import { NomObjectClass, NomObjectTypes } from 'src/app/core/constants';
import { ContentFilters, Post } from 'src/app/core/models/core.models';

import {
  catchError,
  distinctUntilChanged,
  from,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { AppState } from '../../../../reducers/index';

import { injectSetPostRelatedPostsMutation } from 'src/app/api/queries/post.queries';
import { selectFeedTimeShown, selectUser } from 'src/app/core/reducers';

interface DialogData {
  post: Post;
  feedId: string;
  feedTypeId: NomObjectTypes;
}
interface DialogOutput {}

type RelationFilterState = 'all' | 'only' | 'exclude';

/** A run of consecutive same-`radioGroup` rows, or a single non-radio row. */
interface ViewDropdownGroup {
  radioGroup?: string;
  items: DropdownItem[];
}

@Component({
  selector: 'app-post-related-dialog',
  standalone: false,
  templateUrl: './post-related-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './post-related-dialog.component.scss',
})
export class PostRelatedDialogComponent extends SimpleDialogBase<
  DialogData,
  DialogOutput
> {
  protected dirtyHandler = injectDirtyCloseHandler(this.dialogRef);

  private destroyRef = inject(DestroyRef);
  private store = inject(Store<AppState>);
  private postApi = inject(PostApiService);
  private feedApi = inject(FeedApiService);
  private feedSettingsService = inject(FeedSettingsService);
  private readonly setRelatedMutation = injectSetPostRelatedPostsMutation();

  dialogTitle = this.translateService.instant('post-related.title.link-posts');
  actionIcon: IconDefinition = faLink;

  postSelectDirty = signal(false);
  isDirty = computed(() => this.formGroup.dirty || this.postSelectDirty());
  isValid = computed(() => this.formGroup.valid);

  customRangeOpen = false;
  NomObjectClass = NomObjectClass;
  NomObjectTypes = NomObjectTypes;
  posts: Post[] = [];
  postsRelated: Post[] = [];
  periods = [
    { id: 'period1h', value: '1h', label: '1h' },
    { id: 'period3h', value: '3h', label: '3h' },
    { id: 'period6h', value: '6h', label: '6h' },
    { id: 'period12h', value: '12h', label: '12h' },
    { id: 'period1d', value: '1d', label: '1d' },
    { id: 'period2d', value: '2d', label: '2d' },
    { id: 'period3d', value: '3d', label: '3d' },
    { id: 'period1w', value: '1w', label: '1w' },
    { id: 'period2w', value: '2w', label: '2w' },
    { id: 'period1m', value: '1m', label: '1m' },
    { id: 'period6m', value: '6m', label: '6m' },
    { id: 'period1y', value: '1y', label: '1y' },
    { id: 'custom', value: 'custom', label: 'Custom' },
  ];
  currentPage = 1;
  pageSize = 30;
  totalItems = 0;
  postViewConfig: PropertyViewConfig[] = [];
  readonly postViewSettings: PropertyViewConfig[] = [
    {
      path: 'content',
      selected: true,
      visibility: ['view-properties'],
    },
  ];
  viewDropdownItems: DropdownItem[] = [];
  viewDropdownGroups: ViewDropdownGroup[] = [];
  timeShown: string = 'post_time_published_absolute';
  sortField: string = 'post_time_published';
  sort: 'asc' | 'desc' = 'desc';
  sortDropdown: DropdownItem[] = [
    {
      text: this.translateService.instant('post-related.button.published-time'),
      radioGroup: 'sort',
      icon: ['fas', 'upload'],
      value: 'post_time_published',
    },
    {
      text: this.translateService.instant('post-related.button.created-time'),
      radioGroup: 'sort',
      icon: ['fas', 'circle-plus'],
      value: 'post_time_created',
    },
    {
      text: this.translateService.instant(
        'post-related.button.potential-reach',
      ),
      radioGroup: 'sort',
      icon: ['fab', 'chromecast'],
      value: 'post_reach',
    },
    {
      text: this.translateService.instant(
        'post-related.button.source-daily-visits',
      ),
      radioGroup: 'sort',
      icon: ['fab', 'chromecast'],
      value: 'post_source_visits_per_day',
    },
    {
      text: this.translateService.instant('post-related.button.content-rank'),
      radioGroup: 'sort',
      icon: ['fas', 'chart-line'],
      value: 'post_rank',
    },
    {
      text: this.translateService.instant('post-related.button.title'),
      radioGroup: 'sort',
      icon: ['fas', 'font'],
      value: 'post_title',
      applicable_object_type_ids: [
        NomObjectTypes.COMBINED_FEED,
        NomObjectTypes.ONLINE_FEED,
        NomObjectTypes.STORY_FEED,
        NomObjectTypes.CLIPPING_FEED,
      ],
    },
    {
      text: this.translateService.instant('post-related.button.global-rank'),
      radioGroup: 'sort',
      icon: ['fas', 'trophy'],
      value: 'source_rank_global',
      applicable_object_type_ids: [
        NomObjectTypes.COMBINED_FEED,
        NomObjectTypes.ONLINE_FEED,
        NomObjectTypes.STORY_FEED,
        NomObjectTypes.CLIPPING_FEED,
      ],
    },
    {
      text: this.translateService.instant('post-related.button.country-rank'),
      radioGroup: 'sort',
      icon: ['fas', 'sort-numeric-up'],
      value: 'source_rank_country',
      applicable_object_type_ids: [
        NomObjectTypes.COMBINED_FEED,
        NomObjectTypes.ONLINE_FEED,
        NomObjectTypes.STORY_FEED,
        NomObjectTypes.CLIPPING_FEED,
      ],
    },
    {
      text: this.translateService.instant('post-related.button.followers'),
      radioGroup: 'sort',
      icon: ['fas', 'users'],
      value: 'post_social_followers',
      applicable_object_type_ids: [
        NomObjectTypes.COMBINED_FEED,
        NomObjectTypes.SOCIAL_FEED,
        NomObjectTypes.CLIPPING_FEED,
      ],
    },
    {
      text: this.translateService.instant(
        'post-related.button.social-engagements',
      ),
      radioGroup: 'sort',
      icon: ['fas', 'hand-point-right'],
      value: 'post_social_engagements_count',
      applicable_object_type_ids: [
        NomObjectTypes.COMBINED_FEED,
        NomObjectTypes.SOCIAL_FEED,
        NomObjectTypes.CLIPPING_FEED,
      ],
    },
    {
      text: this.translateService.instant('post-related.button.social-shares'),
      radioGroup: 'sort',
      icon: ['fas', 'retweet'],
      value: 'post_social_shares_count',
      applicable_object_type_ids: [
        NomObjectTypes.COMBINED_FEED,
        NomObjectTypes.SOCIAL_FEED,
        NomObjectTypes.CLIPPING_FEED,
      ],
    },
    {
      text: this.translateService.instant(
        'post-related.button.social-comments',
      ),
      radioGroup: 'sort',
      icon: ['fas', 'comments'],
      value: 'post_social_comments_count',
      applicable_object_type_ids: [
        NomObjectTypes.COMBINED_FEED,
        NomObjectTypes.SOCIAL_FEED,
        NomObjectTypes.CLIPPING_FEED,
      ],
    },
    {
      text: this.translateService.instant('post-related.button.social-likes'),
      radioGroup: 'sort',
      icon: ['fas', 'heart'],
      value: 'post_social_likes_count',
      applicable_object_type_ids: [
        NomObjectTypes.COMBINED_FEED,
        NomObjectTypes.SOCIAL_FEED,
        NomObjectTypes.CLIPPING_FEED,
      ],
    },
    {
      isSeparator: true,
    },

    {
      text: this.translateService.instant('post-related.button.descending'),
      radioGroup: 'sort-order',
      icon: ['fas', 'sort-amount-down'],
      value: 'desc',
    },
    {
      text: this.translateService.instant('post-related.button.ascending'),
      radioGroup: 'sort-order',
      icon: ['fas', 'sort-amount-up'],
      value: 'asc',
    },
  ];

  // WAP-1508: relationship-status filters, combined with AND when both are active
  relatedFilter: RelationFilterState = 'all';
  parentFilter: RelationFilterState = 'all';
  relatedFilterItems: DropdownItem[] = [
    {
      text: this.translateService.instant('post-related.filter.all-articles'),
      value: 'all',
    },
    {
      text: this.translateService.instant(
        'post-related.filter.show-only-already-related-articles',
      ),
      value: 'only',
    },
    {
      text: this.translateService.instant(
        'post-related.filter.exclude-already-related-articles',
      ),
      value: 'exclude',
    },
  ];
  parentFilterItems: DropdownItem[] = [
    {
      text: this.translateService.instant('post-related.filter.all-articles'),
      value: 'all',
    },
    {
      text: this.translateService.instant(
        'post-related.filter.show-only-parent-articles',
      ),
      value: 'only',
    },
    {
      text: this.translateService.instant(
        'post-related.filter.exclude-parent-articles',
      ),
      value: 'exclude',
    },
  ];

  postsLoading = false;
  faSpinner = faSpinner;
  formGroup: FormGroup;

  set post(value: Post) {
    this.dialogData.post = value;
  }

  get post() {
    return this.dialogData.post;
  }

  get feedId() {
    return this.dialogData.feedId;
  }

  get feedTypeId() {
    return this.dialogData.feedTypeId;
  }

  ngOnInit(): void {
    this.store
      .select(selectFeedTimeShown)
      .pipe(
        tap((timeShown) => {
          this.timeShown = timeShown;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectUser)
      .pipe(
        switchMap((user) =>
          from(this.feedSettingsService.getFeedConfig(this.feedTypeId, user)),
        ),
        tap((config) => {
          this.postViewConfig = config;
          this.buildViewDropdownItems();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    const today = new Date();
    const lastWeek = new Date();
    lastWeek.setDate(today.getDate() - 7);

    this.formGroup = new FormGroup({
      filterQuery: new FormControl('*'),
      searchFeedId: new FormControl(this.feedId),
      period: new FormControl('12h'), // ← default to custom
      customRange: new FormControl<SuiDateRange>(
        { start: lastWeek, end: today },
        { nonNullable: true },
      ),
    });

    this.formGroup
      .get('period')!
      .valueChanges.pipe(
        // only trigger when the period value actually changes
        distinctUntilChanged(),
        // side‑effect: call your handler
        tap((val) => this.onPeriodChange(val)),
        // auto‑unsubscribe when component is destroyed
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    if (this.post && this.post.post_related_to_parent_id?.length) {
      this.postApi
        .getPost(this.post.post_related_to_parent_id[0], this.feedId)
        .pipe(
          // only need the first emission
          take(1),
          // side‑effect: update component state
          tap((parent) => {
            this.post = parent;
            this.postsRelated =
              parent.related_posts.map(
                (post) => (post = { ...post, selected: true }),
              ) ?? [];
          }),
          // ensure cleanup if component is destroyed before the response arrives
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    } else {
      this.postsRelated =
        this.post.related_posts.map(
          (post) => (post = { ...post, selected: true }),
        ) ?? [];
    }

    if (this.feedTypeId) {
      this.sortDropdown = this.sortDropdown.filter(
        (item) =>
          !item.applicable_object_type_ids ||
          item.applicable_object_type_ids.includes(this.feedTypeId),
      );
    }

    this.search();
  }

  ngAfterViewInit(): void {
    this.setupDirtyHandler(this.isDirty, this.isValid);
  }

  onPeriodChange(value: string) {
    this.customRangeOpen = value === 'custom';
  }

  // Reorder the selected related posts via drag-and-drop. The order here is the
  // order persisted by setRelated() (relatedPostIds is built from postsRelated).
  onRelatedDrop(event: CdkDragDrop<Post[]>) {
    if (event.previousIndex === event.currentIndex) return;

    const reordered = [...this.postsRelated];
    moveItemInArray(reordered, event.previousIndex, event.currentIndex);
    this.postsRelated = reordered;

    this.postSelectDirty.set(true);
  }

  togglePostSelect(post: Post) {
    return () => {
      const id = post.id;
      const selected = !post.selected;
      const updated = { ...post, selected };

      this.posts = this.posts.map((p) => (p.id === id ? updated : p));

      const existsInRelated = this.postsRelated.some((p) => p.id === id);

      if (selected && !existsInRelated) {
        this.postsRelated = [...this.postsRelated, updated];
      }

      if (!selected) {
        this.postsRelated = this.postsRelated.filter((p) => p.id !== id);
      }

      this.postSelectDirty.set(true);
    };
  }

  // Select-all reflects only the current page of results — pagination re-runs the
  // search, so the checkbox is derived from `posts` rather than held as state.
  get allPageSelected(): boolean {
    return this.posts.length > 0 && this.posts.every((p) => p.selected);
  }

  get somePageSelected(): boolean {
    return this.posts.some((p) => p.selected) && !this.allPageSelected;
  }

  // Link (or unlink) every post on the current page in one action. Partial
  // selection resolves to "select the rest", matching native tri-state behaviour.
  toggleSelectAllOnPage(): void {
    const select = !this.allPageSelected;

    this.posts = this.posts.map((post) => ({ ...post, selected: select }));

    if (select) {
      const relatedIds = new Set(this.postsRelated.map((p) => p.id));
      // Append in page order, preserving the existing related order (which is
      // user-sortable via drag-and-drop) and skipping already-linked posts.
      this.postsRelated = [
        ...this.postsRelated,
        ...this.posts.filter((post) => !relatedIds.has(post.id)),
      ];
    } else {
      const pageIds = new Set(this.posts.map((p) => p.id));
      this.postsRelated = this.postsRelated.filter((p) => !pageIds.has(p.id));
    }

    this.postSelectDirty.set(true);
  }

  // * Retrieves the content filters based on the form group values
  // * returns -> the content filters object
  getFormContentFilters(): ContentFilters {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    const currentTime = toUnix(now);

    const periodValue = this.formGroup.get('period').value;

    let time_start = 0;
    let time_end = currentTime;

    if (periodValue === 'custom') {
      const { start: startDate, end: endDate } =
        this.formGroup.get('customRange').value;

      if (startDate && endDate) {
        // * sui-date-range-picker emits startOfDay Dates; apply endOfDay for an
        // * inclusive whole-day range (start 00:00:00 → end 23:59:59).
        time_start = toUnix(startDate);
        time_end = toUnix(endOfDay(endDate));
      }
    } else {
      const match = periodValue.match(/(\d+)([hdwmy])/);
      if (match) {
        const value = parseInt(match[1], 10);
        const unit = match[2];
        const unitMultiplier = {
          h: 3600,
          d: 86400,
          w: 604800,
          m: 2592000,
          y: 31536000,
        };

        time_start = currentTime - value * unitMultiplier[unit];
      }
    }

    return {
      filter_query: this.getFilterQueryWithRelationConditions(),
      time_start,
      time_end,
      offset: (this.currentPage - 1) * this.pageSize,
      items_count: 30,
      sort: this.sort,
      selected_only: false,
      order: this.sortField,
    };
  }

  // Combines the user query with the relationship-status filter conditions.
  // Related-child status maps to the `is_related_child` query operator and
  // parent status to `has_related`; active conditions are AND-ed onto the
  // user query (wrapped in parentheses so OR queries keep their grouping).
  getFilterQueryWithRelationConditions(): string {
    const baseQuery =
      (this.formGroup.get('filterQuery').value || '').trim() || '*';

    const conditions: string[] = [];

    if (this.relatedFilter === 'only') {
      conditions.push('is_related_child:1');
    } else if (this.relatedFilter === 'exclude') {
      conditions.push('NOT is_related_child:1');
    }

    if (this.parentFilter === 'only') {
      conditions.push('has_related:1');
    } else if (this.parentFilter === 'exclude') {
      conditions.push('NOT has_related:1');
    }

    if (!conditions.length) {
      return baseQuery;
    }

    const wrappedQuery = baseQuery === '*' ? baseQuery : `(${baseQuery})`;

    return `${wrappedQuery} AND ${conditions.join(' AND ')}`;
  }

  // Updates a relationship-status filter group and re-runs the search from page 1
  onFilterChange(group: 'related' | 'parent', value: RelationFilterState) {
    const target = group === 'related' ? 'relatedFilter' : 'parentFilter';

    if (this[target] === value) return;

    this[target] = value;
    this.currentPage = 1;
    this.search();
  }

  // Whether any relationship-status filter is active (used to highlight the button)
  get relationFilterActive(): boolean {
    return this.relatedFilter !== 'all' || this.parentFilter !== 'all';
  }

  // * Performs a search for feed posts based on the search feed ID and content filters.
  search() {
    this.postsLoading = true;
    this.feedApi
      .getFeedPosts(
        this.formGroup.get('searchFeedId').value,
        this.getFormContentFilters(),
        true,
      )
      .pipe(
        tap((response) => {
          this.postsLoading = false;
          // backend can return selected flags here and we want to reset them
          this.posts = (response.posts || [])
            .filter((post) => post.id !== this.post?.id)
            .map((post) => ({
              ...post,
              selected: this.postsRelated.some(
                (postItem) => postItem.id === post.id,
              ),
            }));

          this.totalItems = response.count_posts;
        }),
        catchError((error) => {
          console.error('Error fetching posts', error);

          this.postsLoading = false;

          return of({ posts: [] });
        }),
      )
      .subscribe();
  }

  togglePostParent(post: Post) {
    // Remove the clicked post from the related list
    this.postsRelated = this.postsRelated.filter((p) => p.id !== post.id);

    // Move the current main post into the related list (if it exists)
    if (this.post) {
      this.postsRelated.push(this.post);
    }

    // Set the clicked post as the new main post
    this.post = post;
  }

  setRelated() {
    this.setRelatedMutation.mutate({
      postId: this.post.id,
      relatedPostIds: this.postsRelated.map((p) => p.id),
      feedId: this.feedId,
    });
  }

  onPageChange(page: number | string) {
    if (typeof page === 'string') {
      page = parseInt(page, 10) || 1;
    }
    this.currentPage = page;
    this.search();
  }

  closeCustomDropdown() {
    this.customRangeOpen = false;
    //    this.cdr.detectChanges();
    this.search();
  }

  onPeriodClick() {
    if (
      this.formGroup.get('period').value === 'custom' &&
      !this.customRangeOpen
    ) {
      this.customRangeOpen = true;
    }
  }

  // Updates sort or sortField based on dropdown selection and triggers a new search
  onSortChange(item: DropdownItem) {
    const target = item.radioGroup === 'sort' ? 'sortField' : 'sort';

    if (this[target] === item.value) return;

    this[target] = item.value;
    this.search();
  }

  buildViewDropdownItems() {
    const items: DropdownItem[] = [];

    const tools = this.postViewConfig.filter((c) =>
      c.visibility?.includes('tools'),
    );
    const properties = this.postViewConfig.filter(
      (c) => !c.visibility?.includes('tools'),
    );

    const addItems = (configs: PropertyViewConfig[]) => {
      for (const cfg of configs) {
        if (cfg.isTitle) {
          items.push({ isSeparator: true }, { text: cfg.label, isTitle: true });
        } else if (cfg.radioGroup === 'view_posts_mode') {
          // Skip view mode radios — not relevant in dialog context
          continue;
        } else {
          items.push({
            text: cfg.label,
            icon: cfg.icon as any,
            isToggle: !cfg.isRadio,
            isRadio: cfg.isRadio,
            radioGroup: cfg.radioGroup,
            isSelected: cfg.selected,
            value: cfg.path,
            key: cfg.path as string,
          });
        }
      }
    };

    addItems(tools);
    if (properties.length > 0) {
      items.push(
        { isSeparator: true },
        {
          text: this.translateService.instant('feed-actions.heading.metadata'),
          isTitle: true,
        },
      );
      addItems(properties);
    }

    // Remove leading separator
    if (items.length > 0 && items[0].isSeparator) {
      items.shift();
    }

    this.viewDropdownItems = items;
    this.viewDropdownGroups = this.groupViewItems(items);
  }

  /** Group consecutive same-`radioGroup` rows so each run gets one radiogroup wrapper. */
  private groupViewItems(items: DropdownItem[]): ViewDropdownGroup[] {
    const groups: ViewDropdownGroup[] = [];

    for (const item of items) {
      const previous = groups[groups.length - 1];
      const runs =
        !!item.radioGroup && previous?.radioGroup === item.radioGroup;

      if (runs) {
        previous.items.push(item);
        continue;
      }

      groups.push({ radioGroup: item.radioGroup, items: [item] });
    }

    return groups;
  }

  /** A radio run owns the whole group's selection; delegate to the picked row. */
  onViewRadioChange(group: ViewDropdownGroup, value: unknown): void {
    const item = group.items.find((i) => i.value === value);
    if (item) {
      this.onViewChange(item);
    }
  }

  onViewChange(item: DropdownItem) {
    if (item.isRadio) {
      // For radio items (e.g. post_time_mode), update all items in the group
      const group = item.radioGroup;
      const configItem = this.postViewConfig.find((c) => c.path === item.value);
      if (configItem && group === 'post_time_mode') {
        this.postViewConfig = this.postViewConfig.map((c) =>
          c.radioGroup === group
            ? { ...c, selected: c.path === item.value }
            : c,
        );
        this.timeShown = item.value as string;
      }
    } else {
      // Toggle selected on the matching config item
      this.postViewConfig = this.postViewConfig.map((c) =>
        c.label === item.text ? { ...c, selected: !c.selected } : c,
      );
      // Update the dropdown item's selected state
      item.isSelected = !item.isSelected;
    }

    this.buildViewDropdownItems();
  }

  protected override performSave(): DialogCloseOptions<DialogOutput> {
    this.setRelated();

    return {
      reason: DialogCloseReason.Save,
    };
  }

  save(): void {
    const result = this.performSave();
    this.dirtyHandler.closeImmediately(result);
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
